import { Component, h, Prop, Element } from '@stencil/core';

/**
 * `me-text` is a text component for displaying inline or block text
 * with different visual styles (variant, weight, and alignment).
 */

const COMPONENT_NAME = 'me-text';
const DEFAULTS = {
  tag: 'p',
  variant: 'regular',
  weight: 'normal',
  align: 'left',
} as const;

/**
 * @slot - {required}, string
 */
@Component({
  tag: 'me-text',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Text {
  /** Reference to the host element (<me-text>) */
  @Element() host: HTMLElement;

  /**
   * The HTML tag to be rendered (`p`, `span`, or `div`).
   * @default "p"
   */
  @Prop() tag: 'p' | 'span' | 'div' = DEFAULTS.tag;

  /**
   * Visual variant (typically font size or spacing) of the text.
   * Maps to specific utility classes like `me-text--small`, etc.
   * @default "regular"
   */
  @Prop() variant: 'tiny' | 'small' | 'regular' | 'medium' | 'large' = DEFAULTS.variant;

  /**
   * Font weight for the text content.
   * @default "normal"
   */
  @Prop() weight: 'extra-bold' | 'bold' | 'semi-bold' | 'medium' | 'normal' | 'light' = DEFAULTS.weight;

  /**
   * Text alignment within the container.
   * @default "left"
   */
  @Prop() align: 'left' | 'right' | 'center' = DEFAULTS.align;

  /**
   * Extracts all attributes from the host element that are not known props,
   * so they can be passed through to the rendered tag.
   *
   * This allows support for native attributes like `id`, `data-*`, `aria-*`, etc.
   * Also handles `innerHTML` attribute which is passed through to render HTML content.
   * Note: innerHTML is not a @Prop() to avoid conflict with native prototype property.
   * Note: itemprop is passed through to the rendered element for microdata support.
   */
  private getExtraAttributes(): Record<string, any> {
    const knownProps = ['tag', 'variant', 'weight', 'align', 'class'];
    const attrs: Record<string, any> = {};

    Array.from(this.host.attributes).forEach(attr => {
      const attrName = attr.name.toLowerCase();
      if (!knownProps.includes(attrName)) {
        // Stencil converts camelCase JSX attributes to lowercase HTML attributes
        // e.g., itemProp -> itemprop, itemScope -> itemscope, itemType -> itemtype
        // innerHTML -> innerhtml
        // So we can use the attribute name as-is
        attrs[attr.name] = attr.value;
      }
    });

    return attrs;
  }

  render() {
    // Use the specified tag (p, span, or div) for rendering
    const Tag = this.tag;

    // Only apply class modifiers if the value differs from the default
    const variantClass = this.variant !== DEFAULTS.variant ? `${COMPONENT_NAME}--${this.variant}` : '';

    const weightClass = this.weight !== DEFAULTS.weight ? `${COMPONENT_NAME}--weight-${this.weight}` : '';

    const alignClass = this.align !== DEFAULTS.align ? `${COMPONENT_NAME}--${this.align}` : '';

    // Combine all classes into one string, filtering out empty values
    const classList = [COMPONENT_NAME, variantClass, weightClass, alignClass].filter(Boolean).join(' ');

    // Get extra attributes to pass through (e.g., itemprop, itemscope, innerHTML, etc.)
    // innerHTML will be automatically included if present on host element
    const extraAttrs = this.getExtraAttributes();

    // Remove itemprop from host element after passing it to rendered element
    // This prevents duplicate itemprop attributes (one on <me-text>, one on rendered element)
    if (this.host.hasAttribute('itemprop')) {
      this.host.removeAttribute('itemprop');
    }

    // Render the element with dynamic tag, computed class list, extra attributes
    // In Stencil, innerHTML as JSX attribute automatically renders HTML content
    return (
      <Tag class={classList} {...extraAttrs}>
        <slot></slot>
      </Tag>
    );
  }
}
